import { Component, DestroyRef, inject, OnDestroy, OnInit } from '@angular/core';
import { SegmentDetail, SegmentDetailResponse } from '../../../../../../core/interface';
import { QueryParamsService } from '../../../../../../core/services/query-params.service';
import { AdxadAlerts } from '../../../../../../ui/modules/alerts/components/alerts/alerts.component';
import { MessageService } from '../../../../../../core/services/message.service';
import { AdxadSidebarModalConfig } from '../../../../../../ui/modules/sidebar-modal/sidebar-modal-config';
import { AdxadSidebarModalRef } from '../../../../../../ui/modules/sidebar-modal/sidebar-modal-ref';
import { AdxadModal } from '../../../../../../ui/modules/modal/modal.service';
import { SegmentsService } from '../../segments.service';
import { SegmentFormComponent } from '../segment-form/segment-form.component';
import { BreadcrumbsService } from '../../../../../../core/services/breadcrumbs.service';
import { AdxadSidebarModal } from '../../../../../../ui/modules/sidebar-modal/sidebar-modal.service';
import { Status } from 'src/app/core/configs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'adxad-segments-view',
  templateUrl: './segment-view.component.html',
  styleUrls: ['./segment-view.component.scss']
})
export class SegmentViewComponent implements OnInit, OnDestroy {
  public isLoading = false;
  public isLoadingStatus = false;
  public segment: SegmentDetail;

  public readonly Status = Status;

  private destroyRef = inject(DestroyRef);

  constructor(
    private queryParamsService: QueryParamsService,
    private alerts: AdxadAlerts,
    private messageService: MessageService,
    private config: AdxadSidebarModalConfig,
    private modalRef: AdxadSidebarModalRef,
    private modal: AdxadModal,
    private segmentsService: SegmentsService,
    public breadcrumbs: BreadcrumbsService,
    private sidebarModal: AdxadSidebarModal
  ) {}

  ngOnInit(): void {
    if (!this.config || !this.config.data || !this.config.data.id) {
      this.alerts.error('Oops something went wrong', 3000);
      this.close();
    }

    this.queryParamsService.add('sm_segmentView', this.config.data.id);
    this.loadData();
  }

  /**
   * Load project data
   */
  loadData(): void {
    if (this.isLoading) {
      return;
    }
    this.isLoading = true;

    this.segmentsService
      .getSegment({ id: this.config.data.id })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result: SegmentDetailResponse) => {
          this.isLoading = false;

          if (result.status === 'OK') {
            this.segment = result.data as SegmentDetail;
          }
        },
        error: () => this.close()
      });
  }

  /**
   * Open edit segment form in modal
   */
  openEditForm(): void {
    this.modal
      .open(SegmentFormComponent, {
        width: '1120px',
        data: { id: this.config.data.id }
      })
      .afterClosed.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        if (value && value.submit) {
          this.loadData();
        }
      });
  }

  /**
   * Activate or stop segment
   *
   * @param {number} status
   */
  changeStatus(status: number): void {
    if (status === undefined || this.isLoadingStatus) {
      return;
    }

    this.isLoadingStatus = true;
    const data = {
      ids: [this.config.data.id],
      status: status
    };

    this.segmentsService
      .changeSegmentsStatus(data)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: result => {
          this.isLoadingStatus = false;

          if (result.status === 'OK') {
            this.messageService.add('reload-segments-grid', { submit: true });

            this.segment.status = result.data[0].status;
            const message = 'The segment is ' + (this.segment.status === this.Status.stopped ? 'stopped' : 'activated');
            this.alerts.success(message, 3000);
          }
        },
        error: () => (this.isLoadingStatus = false)
      });
  }

  /**
   * Close modal
   * if submit == true, grid will reload
   *
   * @param {boolean} submit
   */
  close(submit?): void {
    this.modalRef.close({ submit: submit });
  }

  /**
   * Close segment view component;
   * Open previous component
   */
  goBack(): void {
    this.close();

    const back = this.breadcrumbs.backStep;
    if (!back) {
      return;
    }
    this.breadcrumbs.removeLastStep();

    this.sidebarModal.open(back.component, {
      data: {
        id: back.id
      }
    });
  }

  lineBreak(items) {
    return items.join('\n');
  }

  ngOnDestroy(): void {
    this.queryParamsService.remove('sm_segmentView');
  }
}
